/**
 * GameEngine — imperative core, deliberately outside React's render cycle.
 * React/renderers subscribe to snapshots; the fixed-step physics loop never
 * waits on React.
 */
import { PHYSICS_DT, type RenderMode, type GamePhase } from './types';
import type { TableDefinition, TablePart, Vec2 } from './tables/definition';
import { PinballWorld, type PhysicsEvent } from './physics/world';
import { ScoreTracker } from './scoring';
import { RuleTracker, type RuleSnapshot } from './rules';
import { preloadTableExperience } from '../assets/tablePreload';

export interface TargetPresentation { active: boolean; destroyed: boolean; remainingHits?: number }

export interface FrameSnapshot {
  ball: { x: number; y: number; angle: number; inPlunger: boolean; elevation?: number } | null;
  targets?: Readonly<Record<string,TargetPresentation>>;
  flippers: { x: number; y: number; angle: number; length: number; side: 'left' | 'right' }[];
  plungerCharge: number;
  nudgesRemaining: number;
  score: number;
  multiplier: number;
  scoreBoostRemaining?: number;
  scoreBoostMultiplier?: number;
  tableShake: number;
  ballsRemaining: number;
  ballSaver: boolean;
  tilted: boolean;
  phase: GamePhase;
  rules?: RuleSnapshot;
}

type NonScorePhysicsEvent = Exclude<PhysicsEvent, { type: 'score' }>;

export type GameEvent =
  | NonScorePhysicsEvent
  | { type: 'slingshot'; partId: string }
  | { type: 'flipper'; side: 'left' | 'right' }
  | { type: 'plunger' }
  | { type: 'nudge' }
  | { type: 'ball-save' }
  | { type: 'game-start' }
  | { type: 'game-over' }
  | { type: 'jackpot' };

type SnapshotListener = (snapshot: FrameSnapshot) => void;
type EventListener = (event: GameEvent) => void;

type DisposablePhysicsWorld = Pick<PinballWorld, 'free'>;

export function freePhysicsWorldSafely(world: DisposablePhysicsWorld): void {
  try {
    world.free();
  } catch {
    // The world has already been detached from the live engine by the time this
    // deferred cleanup runs. A native/WASM disposal failure must not surface as
    // an uncaught task error or interfere with the replacement table session.
  }
}

const NUDGE_FLIPPER_LOCKOUT_SECONDS = 1;
const MAX_RUNTIME_PHYSICS_STEPS = 12;

export class GameEngine {
  private tableDef: TableDefinition | null = null;
  private partsById = new Map<string, TablePart>();
  private world: PinballWorld | null = null;
  private accumulator = 0;
  private lastTime = 0;
  private simulationTime = 0;
  private rafId: number | null = null;
  private running = false;
  private loadGeneration = 0;
  private listeners = new Set<SnapshotListener>();
  private phase: GamePhase = 'boot';
  private scoring = new ScoreTracker();
  private rules = new RuleTracker();
  private ballsRemaining = 0;
  private tableShake = 0;
  private ballSaverRemaining = 0;
  private scoreBoostRemaining = 0;
  private scoreBoostMultiplier = 1;
  private nudgeFlipperLockoutRemaining = 0;
  private eventListeners = new Set<EventListener>();
  private flipperHeld = { left: false, right: false };
  private plungerHeld = false;
  private targetPresentation: Record<string,TargetPresentation> = {};

  private resetTargetPresentation(): void {
    this.targetPresentation=Object.fromEntries((this.tableDef?.parts ?? []).flatMap(part =>
      part.kind === 'target' ? [[part.id,{active:!part.appearsAfterPartId,destroyed:false,remainingHits:part.hitPoints}]] : []));
  }

  private stepPhysics(dt: number): void {
    if (!this.world) return;
    this.simulationTime += dt;
    this.tableShake = Math.max(0, this.tableShake - dt * 5);
    this.ballSaverRemaining = Math.max(0, this.ballSaverRemaining - dt);
    this.scoreBoostRemaining = Math.max(0, this.scoreBoostRemaining - dt);
    if (this.scoreBoostRemaining <= 0) this.scoreBoostMultiplier = 1;
    this.scoring.setTemporaryMultiplier(this.scoreBoostRemaining > 0 ? this.scoreBoostMultiplier : 1);
    this.nudgeFlipperLockoutRemaining = Math.max(0, this.nudgeFlipperLockoutRemaining - dt);
    if (this.plungerHeld) this.world.chargePlunger(dt);
    this.world.step();
    this.processEvents(this.world.takeEvents());
  }

  private applyRuleEvent(event: GameEvent): void {
    const outcomes = [this.rules.apply(event, this.simulationTime)];
    if (event.type === 'target' && event.destroyed) {
      outcomes.push(this.rules.apply({ type: 'prop-destroy', partId: event.partId }, this.simulationTime));
    }
    let jackpot = false;
    for (const outcome of outcomes) {
      this.scoring.addBonus(outcome.bonus);
      jackpot ||= outcome.jackpot;
    }
    if (jackpot) this.emitEvent({ type: 'jackpot' });
  }

  private activateScoreBoost(seconds: number, multiplier: number): void {
    if (!Number.isFinite(seconds) || seconds <= 0) return;
    const safeMultiplier = Number.isFinite(multiplier) ? Math.max(1, multiplier) : 1;
    this.scoreBoostRemaining = Math.max(this.scoreBoostRemaining, seconds);
    this.scoreBoostMultiplier = Math.max(this.scoreBoostMultiplier, safeMultiplier);
    this.scoring.setTemporaryMultiplier(this.scoreBoostMultiplier);
  }

  private processEvents(events: PhysicsEvent[]): void {
    let drainHandled = false;
    for (const event of events) {
      this.scoring.apply(event);

      if (event.type === 'score') {
        const part = this.partsById.get(event.partId);
        if (part?.kind === 'slingshot') {
          const semantic: GameEvent = { type: 'slingshot', partId: part.id };
          this.emitEvent(semantic);
          this.applyRuleEvent(semantic);
          this.tableShake = Math.max(this.tableShake, 0.12);
        }
        continue;
      }

      if (event.type === 'kicker') {
        this.activateScoreBoost(event.boostSeconds, event.boostMultiplier);
      } else if (event.type === 'rollover') {
        const part = this.partsById.get(event.partId);
        if (part?.kind === 'rollover') {
          this.activateScoreBoost(
            part.scoreBoostSeconds ?? 0,
            part.scoreBoostMultiplier ?? 1,
          );
        }
      }

      if (event.type === 'target' || event.type === 'target-activated') {
        const old=this.targetPresentation[event.partId];
        this.targetPresentation={...this.targetPresentation,[event.partId]:{
          active:event.type === 'target-activated' || !(event.destroyed || event.dropped),
          destroyed:event.type === 'target' ? Boolean(event.destroyed || event.dropped) : old?.destroyed ?? false,
          remainingHits:event.type === 'target' ? event.remainingHits : old?.remainingHits,
        }};
      }

      this.emitEvent(event);
      this.applyRuleEvent(event);
      if (event.type === 'bumper') this.tableShake = Math.max(this.tableShake, 0.2);
      if (event.type === 'ramp') this.tableShake = Math.max(this.tableShake, 0.08);
      if (event.type === 'kicker') this.tableShake = Math.max(this.tableShake, 0.16);
      if (event.type === 'target') this.tableShake = Math.max(this.tableShake, 0.07);

      if (event.type === 'drain' && !drainHandled) {
        drainHandled = true;
        this.onDrain();
        break;
      }
    }
  }

  private ballSaverDuration(): number {
    return Math.max(0, this.tableDef?.board.ballSaverSeconds ?? 0);
  }

  private resetControlEdges(): void {
    this.flipperHeld.left = false;
    this.flipperHeld.right = false;
    this.plungerHeld = false;
  }

  private resetScoreBoost(): void {
    this.scoreBoostRemaining = 0;
    this.scoreBoostMultiplier = 1;
    this.scoring.setTemporaryMultiplier(1);
  }

  private serveBall(ballSaver: boolean): void {
    this.world?.serveBall();
    this.ballSaverRemaining = ballSaver ? this.ballSaverDuration() : 0;
    this.nudgeFlipperLockoutRemaining = 0;
    this.resetScoreBoost();
    this.resetControlEdges();
  }

  private onDrain(): void {
    if (this.ballSaverRemaining > 0) {
      this.serveBall(false);
      this.emitEvent({ type: 'ball-save' });
      return;
    }

    this.ballsRemaining = Math.max(0, this.ballsRemaining - 1);
    if (this.ballsRemaining > 0) {
      this.serveBall(true);
      return;
    }

    this.phase = 'game-over';
    this.emitEvent({ type: 'game-over' });
    this.stop();
  }

  private disposeWorld(world: PinballWorld): void {
    globalThis.setTimeout(() => freePhysicsWorldSafely(world), 0);
  }

  private runLoop(emitGameStart: boolean): void {
    if (this.running || !this.world) return;
    this.running = true;
    this.rafId = null;
    this.accumulator = 0;
    this.phase = 'playing';
    this.lastTime = performance.now();
    if (emitGameStart) this.emitEvent({ type: 'game-start' });
    this.emit();

    const tick = (now: number): void => {
      if (!this.running) return;
      const frame = Math.min(Math.max((now - this.lastTime) / 1000, 0), 0.1);
      this.lastTime = now;
      this.advancePhysics(frame, MAX_RUNTIME_PHYSICS_STEPS);
      this.emit();
      if (!this.running) {
        this.rafId = null;
        return;
      }
      this.rafId = requestAnimationFrame(tick);
    };

    this.rafId = requestAnimationFrame(tick);
  }

  async loadTable(table: TableDefinition): Promise<void> {
    const generation = ++this.loadGeneration;
    this.stop();
    this.phase = 'boot';

    const previousWorld = this.world;
    this.world = null;
    if (previousWorld) this.disposeWorld(previousWorld);

    this.tableDef = table;
    this.resetTargetPresentation();
    this.partsById = new Map(table.parts.map((part) => [part.id, part]));
    this.scoring.reset(table);
    this.rules.reset(table.rules);
    this.simulationTime = 0;
    this.ballsRemaining = table.board.ballsPerGame;
    this.tableShake = 0;
    this.ballSaverRemaining = 0;
    this.resetScoreBoost();
    this.nudgeFlipperLockoutRemaining = 0;
    this.accumulator = 0;
    this.resetControlEdges();

    const worldPromise = PinballWorld.create(table);
    void preloadTableExperience(table).catch(() => {
      // Asset/audio preload is best-effort and intentionally not part of the
      // playable-world readiness barrier. Runtime render/audio paths already
      // fail open if a warmup is still pending or unexpectedly rejects.
    });
    const world = await worldPromise;
    if (generation !== this.loadGeneration) {
      this.disposeWorld(world);
      return;
    }

    this.world = world;
    this.serveBall(true);
    this.emit();
  }

  get table(): TableDefinition | null {
    return this.tableDef;
  }

  restart(): boolean {
    if (!this.world || !this.tableDef) return false;
    this.stop();
    this.phase = 'boot';
    this.accumulator = 0;
    this.simulationTime = 0;
    this.scoring.reset(this.tableDef);
    this.rules.reset(this.tableDef.rules);
    this.ballsRemaining = this.tableDef.board.ballsPerGame;
    this.tableShake = 0;
    this.resetScoreBoost();
    this.nudgeFlipperLockoutRemaining = 0;
    this.world.reset();
    this.resetTargetPresentation();
    this.ballSaverRemaining = this.ballSaverDuration();
    this.resetControlEdges();
    this.emit();
    return true;
  }

  start(): void {
    this.runLoop(true);
  }

  pause(): boolean {
    if (!this.running || this.phase !== 'playing') return false;
    this.stop();
    this.resetControlEdges();
    this.world?.setFlipper('left', false);
    this.world?.setFlipper('right', false);
    this.phase = 'paused';
    this.emit();
    return true;
  }

  resume(): boolean {
    if (this.running || !this.world || this.phase !== 'paused') return false;
    this.runLoop(false);
    return true;
  }

  togglePause(): boolean {
    return this.phase === 'paused' ? this.resume() : this.pause();
  }

  stop(): void {
    this.running = false;
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.accumulator = 0;
  }

  onSnapshot(listener: SnapshotListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onEvent(listener: EventListener): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  private emitEvent(event: GameEvent): void {
    for (const listener of this.eventListeners) {
      try {
        listener(event);
      } catch {
        // Event listeners are optional observers (audio/UI/analytics). A faulty
        // observer must not interrupt rules, scoring, input handling, or physics.
      }
    }
  }

  private advancePhysics(seconds: number, maxSteps = Number.POSITIVE_INFINITY): void {
    this.accumulator += seconds;
    let steps = 0;
    while (this.accumulator >= PHYSICS_DT && steps < maxSteps) {
      this.stepPhysics(PHYSICS_DT);
      this.accumulator -= PHYSICS_DT;
      steps += 1;
      if (this.phase === 'game-over') {
        this.accumulator = 0;
        break;
      }
    }

    if (steps >= maxSteps && this.accumulator >= PHYSICS_DT) {
      this.accumulator %= PHYSICS_DT;
    }
  }

  advance(seconds: number): void {
    if (!Number.isFinite(seconds) || seconds <= 0) {
      this.emit();
      return;
    }
    this.advancePhysics(seconds);
    this.emit();
  }

  private emit(): void {
    const snapshot: FrameSnapshot = {
      ball: this.world ? this.world.ballState() : null,
      targets:this.targetPresentation,
      flippers: this.world ? this.world.flipperStates() : [],
      plungerCharge: this.world?.charge ?? 0,
      nudgesRemaining: this.world?.nudges ?? 0,
      score: this.scoring.score,
      multiplier: this.scoring.multiplier,
      scoreBoostRemaining: this.scoreBoostRemaining,
      scoreBoostMultiplier: this.scoreBoostMultiplier,
      tableShake: this.tableShake,
      ballsRemaining: this.ballsRemaining,
      ballSaver: this.ballSaverRemaining > 0,
      tilted: this.world?.isTilted ?? false,
      phase: this.phase,
      rules: this.rules.snapshot(this.simulationTime),
    };
    for (const listener of this.listeners) {
      try {
        listener(snapshot);
      } catch {
        // Snapshot subscribers are outside the simulation core. Keep later
        // observers and the fixed-step loop alive if one consumer fails.
      }
    }
  }

  get currentPhase(): GamePhase {
    return this.phase;
  }

  setFlipper(side: 'left' | 'right', engaged: boolean): void {
    if (!this.world) return;
    if (this.phase !== 'playing') {
      this.flipperHeld[side] = false;
      this.world.setFlipper(side, false);
      return;
    }
    if (this.flipperHeld[side] !== engaged) {
      if (engaged) this.emitEvent({ type: 'flipper', side });
      this.flipperHeld[side] = engaged;
    }
    this.world.setFlipper(side, this.nudgeFlipperLockoutRemaining > 0 ? false : engaged);
  }

  chargePlunger(frameDt: number): void {
    void frameDt;
    if (!this.world || this.phase !== 'playing') return;
    this.plungerHeld = true;
  }

  releasePlunger(): void {
    if (!this.world) return;
    if (this.phase !== 'playing') {
      this.plungerHeld = false;
      return;
    }
    if (!this.plungerHeld) return;
    this.plungerHeld = false;
    this.world.releasePlunger();
    this.emitEvent({ type: 'plunger' });
  }

  nudge(direction: Vec2): boolean {
    const world = this.world;
    if (this.phase !== 'playing' || !world) return false;

    const depleted = world.nudges <= 0;
    const accepted = world.nudge(direction);
    this.processEvents(world.takeEvents());
    if (accepted) {
      this.tableShake = Math.max(this.tableShake, depleted ? 0.32 : 1);
      if (depleted) {
        this.nudgeFlipperLockoutRemaining = NUDGE_FLIPPER_LOCKOUT_SECONDS;
        world.setFlipper('left', false);
        world.setFlipper('right', false);
      }
      this.emitEvent({ type: 'nudge' });
    }
    return accepted;
  }

  setRenderMode(mode: RenderMode): void {
    void mode;
  }
}

export const engine = new GameEngine();
